import {
  CASE_LINKS_KIND,
  CASE_STORY_KIND,
  LINKABLE_KINDS,
  caseLinksSignature,
  caseSignature,
  isRecordEnrichment,
  legacyRecordSignature,
  recordSignature,
  relatedPermitKey,
  validateCaseLinkProposals,
  type AiPropertyContext,
  type AiRecord,
  type CaseAiRecord,
  type CaseLinksEnrichment,
  type CaseStoryEnrichment,
} from './ai-artifacts';
import { isDistrictWideRecording } from './assessment-reconciliation';
import {
  deriveCaseLinks,
  recordKey,
  type CaseGraph,
  type CaseLink,
  type PropertyCase,
} from './cases';
import { groupLiens, lienClearanceDates } from './liens';
import { caseRecordInput, propertyCases } from './property-cases';
import { isNovBoilerplate, novSummary, recordThread } from './property-feed';
import {
  displayRecords,
  isPermit,
  lienStatus,
  needsAttention,
  propertyLienMatches,
  sf311OwnerFacing,
  type DisplayRecord,
} from './property-records';
import { publicWorksPlacement } from './public-works-display';
import { documentTime } from './record-dates';
import type { PropertySummary } from './types';
import { eligibleForAiSummary } from './ai-summary-eligibility';
import type { OfficialTaxBills } from './property-view-types';
import { taxBillRecords } from '../skills/sf-treasurer/scripts/presentation';

const MAX_CONTEXT_EVENTS = 12;
const MAX_RELATED = 8;
/** Records offered to the link prompt per property; newest current work first. */
export const MAX_LINK_CANDIDATES = 40;

function threadEvents(record: DisplayRecord): CaseAiRecord['events'] {
  return recordThread(record)
    .filter((entry) => entry.date || entry.comment || entry.status)
    .sort((a, b) => (documentTime(b.date) ?? 0) - (documentTime(a.date) ?? 0))
    .slice(0, MAX_CONTEXT_EVENTS)
    .map((entry) => ({
      date: entry.date,
      label: entry.label,
      status: entry.status,
      comment: entry.comment.slice(0, 400),
    }));
}

/**
 * What the rest of the property says about each record: case-layer links,
 * the record's own dated activity, whether a lien predates a later deed or
 * loan, and whether a 311 category is one the owner answers for.
 */
export function propertyContexts(summary: PropertySummary, records = displayRecords(summary)) {
  const byKey = new Map(records.map((record) => [recordKey(record), record]));
  const links = deriveCaseLinks(records.map(caseRecordInput), {
    now: 0,
    parseDate: documentTime,
  });
  const linksByKey = new Map<string, CaseLink[]>();
  for (const link of links) {
    for (const key of [link.source, link.target]) {
      const list = linksByKey.get(key) ?? [];
      list.push(link);
      linksByKey.set(key, list);
    }
  }
  const events = new Map(records.map((record) => [recordKey(record), threadEvents(record)]));
  const documents = summary.recorder?.documents ?? [];
  const clearances = lienClearanceDates(documents);

  const contexts = new Map<string, AiPropertyContext>();
  for (const record of records) {
    const key = recordKey(record);
    const related = (linksByKey.get(key) ?? [])
      .map((link) => {
        const otherKey = link.source === key ? link.target : link.source;
        const other = byKey.get(otherKey);
        if (!other) return null;
        return {
          key: otherKey,
          relation: link.relation,
          origin: link.origin,
          kind: other.kind,
          id: other.id,
          status: other.status,
          date: other.date,
          description: (other.description ?? '').slice(0, 300),
          archived: other.archived,
          latestEvent: events.get(otherKey)?.[0] ?? null,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => (documentTime(b.date) ?? 0) - (documentTime(a.date) ?? 0))
      .slice(0, MAX_RELATED);
    const context: AiPropertyContext = {
      relatedRecords: related,
      events: events.get(key) ?? [],
    };
    if (record.kind === 'recorder' && record.lien?.priorOwner) context.priorOwner = true;
    if (record.kind === 'recorder' && record.lien && !record.lien.released) {
      const recorded = documentTime(record.lien.documents[0]?.date) ?? 0;
      const clearing = documents
        .filter((document) => {
          const time = documentTime(document.documentDate);
          return time !== null && time >= recorded && clearances.includes(time);
        })
        .sort(
          (a, b) => (documentTime(a.documentDate) ?? 0) - (documentTime(b.documentDate) ?? 0),
        )[0];
      context.clearedBy = clearing
        ? { date: clearing.documentDate, titles: clearing.titles }
        : null;
    }
    if (record.kind === '311') context.ownerFacingCategory = sf311OwnerFacing(record);
    if (record.kind === 'public-works') context.streetWorkPlacement = publicWorksPlacement(record);
    contexts.set(key, context);
  }
  return contexts;
}

/** The shared canonical source for worker generation and timeline cache matching. */
export function aiRecords(summary: PropertySummary): AiRecord[] {
  const contexts = propertyContexts(summary);
  return baseAiRecords(summary).map((record) => {
    const property = contexts.get(`${record.kind}:${record.id}`);
    return property ? { ...record, property } : record;
  });
}

/** Keep canonical evidence intact; only summary targets use the activity policy. */
export function aiSummaryRecordKeys(summary: PropertySummary) {
  return new Set(
    displayRecords(summary)
      .filter((record) => eligibleForAiSummary(record, summary.fetchedAt))
      .map(recordKey),
  );
}

/** Records with their stored AI explanation attached when the signature still matches. */
export function propertyRecords(
  summary: PropertySummary,
  options: { analysisActive?: boolean; taxBills?: OfficialTaxBills } = {},
): DisplayRecord[] {
  const records = [...displayRecords(summary), ...taxBillRecords(options.taxBills)];
  const enriched = new Map(
    (summary.enrichments ?? []).filter(isRecordEnrichment).map((item) => [item.signature, item]),
  );
  const byKey = new Map(records.map((record) => [recordKey(record), record]));
  const eligible = aiSummaryRecordKeys(summary);
  for (const canonical of aiRecords(summary)) {
    const record = byKey.get(`${canonical.kind}:${canonical.id}`);
    if (!record || !eligible.has(recordKey(record))) continue;
    record.ai =
      enriched.get(recordSignature(canonical)) ?? enriched.get(legacyRecordSignature(canonical));
    if (!record.ai && options.analysisActive) record.aiPending = true;
  }
  return records;
}

/**
 * How many of the records that get a summary have one. Unlike `aiPending`,
 * this does not depend on a check being active, so a check that ended early
 * still shows which summaries never arrived.
 */
export function summaryProgress(summary: PropertySummary, records: DisplayRecord[]) {
  const byKey = new Map(records.map((record) => [recordKey(record), record]));
  const eligible = aiSummaryRecordKeys(summary);
  const expected = aiRecords(summary).flatMap((canonical) => {
    const record = byKey.get(`${canonical.kind}:${canonical.id}`);
    return record && eligible.has(recordKey(record)) ? [record] : [];
  });
  return { done: expected.filter((record) => record.ai).length, total: expected.length };
}

export function propertyRecordGroups(summary: PropertySummary) {
  const records = propertyRecords(summary);
  const current = records.filter((record) => !record.archived);
  const permits = current.filter(
    (record) => isPermit(record.kind) && record.activity?.classification === 'recent',
  );
  const unclassifiedPermits = current.filter(
    (record) => isPermit(record.kind) && record.activity?.classification === 'unknown',
  );
  return {
    current,
    history: records.filter((record) => record.archived),
    attention: current.filter(needsAttention),
    permits,
    unclassifiedPermits,
    otherCurrent: current.filter((record) => !needsAttention(record) && !isPermit(record.kind)),
  };
}

/** Records as the case-level prompts see them: headline, status and dated city text. */
export function caseAiRecords(records: DisplayRecord[]): CaseAiRecord[] {
  return records.map((record) => {
    const input = caseRecordInput(record);
    return {
      key: recordKey(record),
      kind: record.kind,
      id: record.id,
      title: [input.title, input.description].filter(Boolean).join(' — ').slice(0, 400),
      status: record.status,
      date: record.date,
      archived: record.archived,
      events: threadEvents(record),
    };
  });
}

function baseAiRecords(summary: PropertySummary): AiRecord[] {
  const timelineRecords = [
    ...(summary.open ?? []).map((record) => ({
      record,
      timelineState: 'current' as const,
    })),
    ...(summary.historicalPermits ?? []).map((record) => ({
      record,
      timelineState: 'historical' as const,
    })),
    ...(summary.recentlyClosed ?? []).map((record) => ({
      record,
      timelineState: 'closed' as const,
    })),
    ...(summary.olderClosed ?? []).map((record) => ({
      record,
      timelineState: 'closed' as const,
    })),
  ];
  const permitGroups = new Map<string, typeof timelineRecords>();
  for (const entry of timelineRecords) {
    const key = relatedPermitKey(entry.record.kind, entry.record.description ?? '');
    if (!key) continue;
    const group = permitGroups.get(key) ?? [];
    group.push(entry);
    permitGroups.set(key, group);
  }
  const records: AiRecord[] = timelineRecords.map(({ record, timelineState }) => {
    const key = relatedPermitKey(record.kind, record.description ?? '');
    const relatedRecords = (permitGroups.get(key) ?? [])
      .filter((entry) => entry.record.id !== record.id)
      .map((entry) => ({
        kind: entry.record.kind,
        id: entry.record.id,
        status: entry.record.status,
        date: entry.record.date,
        description: entry.record.description,
        timelineState: entry.timelineState,
      }));
    return {
      kind: record.kind,
      id: record.id,
      description:
        record.kind === 'nov' && isNovBoilerplate(record.description ?? '')
          ? novSummary({ ...record, archived: false }).headline || record.description
          : record.description,
      status: record.status,
      date: record.date,
      context: relatedRecords.length
        ? {
            recordDetails: record.pages,
            timelineState,
            relatedRecords,
          }
        : record.pages,
    };
  });
  const documents = (summary.recorder?.documents ?? []).filter(
    (record) => !isDistrictWideRecording(record),
  );
  const liens = new Map(
    groupLiens(documents, propertyLienMatches(summary, documents)).map((lien) => [
      lien.document.id,
      lien,
    ]),
  );
  for (const r of documents) {
    const lien = liens.get(r.id);
    records.push({
      kind: 'recorder',
      id: r.documentNumber || r.id,
      description: r.titles.join(', '),
      status: lien ? lienStatus(lien) : '',
      date: lien?.release?.documentDate || r.documentDate,
      context: {
        fields: r.fields,
        names: r.names,
        release: lien?.release ?? null,
        batchReleases: lien?.batchReleases ?? [],
        releaseMatch: lien?.match ?? null,
        ...(lien?.confidence ? { releaseMatchConfidence: lien.confidence } : {}),
      },
    });
  }
  for (const r of summary.subdivision?.projects ?? [])
    records.push({
      kind: 'subdivision',
      id: r.id,
      description: r.name || r.type,
      status: r.status,
      date: r.updatedAt,
      context: { fields: r.fields, activities: r.activities },
    });
  for (const r of summary.sf311?.cases ?? [])
    records.push({
      kind: '311',
      id: r.id,
      description: [r.category, r.details].filter(Boolean).join(' · '),
      status: r.status,
      date: r.updatedAt || r.openedAt,
      context: {
        category: r.category,
        requestType: r.subtype,
        requestDetails: r.details,
        statusNotes: r.statusNotes,
        responsibleAgency: r.agency,
        requestDescription: r.description ?? '',
        requestAttributes: r.attributes ?? {},
        openedAt: r.openedAt,
        closedAt: r.closedAt,
        fields: r.fields,
      },
    });
  for (const r of summary.safetyCompliance?.fireInspections.inspections ?? [])
    records.push({
      kind: 'fire-inspection',
      id: r.id,
      description: r.description || r.type,
      status: r.status,
      date: r.startedAt,
      context: { fields: r.fields, endedAt: r.endedAt },
    });
  for (const r of summary.safetyCompliance?.fireViolations.violations ?? [])
    records.push({
      kind: 'fire-violation',
      id: r.id,
      description: r.description,
      status: r.status,
      date: r.violationDate,
      context: {
        fields: r.fields,
        correctiveAction: r.correctiveAction,
        closeDate: r.closeDate,
      },
    });
  for (const r of summary.safetyCompliance?.section604.affidavits ?? [])
    records.push({
      kind: 'section604',
      id: r.id,
      description: r.comments,
      status: '',
      date: r.inspectionDate,
      context: r.fields,
    });
  for (const r of summary.safetyCompliance?.statements.statements ?? [])
    records.push({
      kind: 'fire-compliance',
      id: r.id,
      description: '',
      status: '',
      date: r.date,
      context: r.fields,
    });
  return [...new Map(records.map((r) => [`${r.kind}:${r.id}`, r])).values()];
}

function byRecency(a: DisplayRecord, b: DisplayRecord) {
  return (documentTime(b.date) ?? 0) - (documentTime(a.date) ?? 0);
}

/**
 * The bounded record set the link prompt sees. Identical in the worker and
 * the page so the stored signature can be matched; empty when there is
 * nothing to relate.
 */
export function caseLinkCandidates(records: DisplayRecord[]) {
  const linkable = records.filter((record) => LINKABLE_KINDS.has(record.kind));
  const ordered = [
    ...linkable.filter((record) => !record.archived).sort(byRecency),
    ...linkable.filter((record) => record.archived).sort(byRecency),
  ].slice(0, MAX_LINK_CANDIDATES);
  return ordered.length >= 2 ? caseAiRecords(ordered) : [];
}

/**
 * Stored model links for this property, accepted only when they were
 * generated from exactly these records and still pass validation.
 */
export function propertyAiLinks(summary: PropertySummary, records: DisplayRecord[]): CaseLink[] {
  const candidates = caseLinkCandidates(records);
  if (!candidates.length) return [];
  const signature = caseLinksSignature(candidates);
  const stored = (summary.enrichments ?? []).find(
    (item): item is CaseLinksEnrichment =>
      item.kind === CASE_LINKS_KIND && item.signature === signature,
  );
  if (!stored) return [];
  return validateCaseLinkProposals(stored.links, candidates, documentTime);
}

/** Cases with model links merged in, when a matching link set is stored. */
export function propertyCaseGraph(
  summary: PropertySummary,
  records: DisplayRecord[],
  now = Date.now(),
  timeZone?: string | null,
) {
  return propertyCases(records, now, propertyAiLinks(summary, records), timeZone);
}

function caseStoryInput(item: PropertyCase, records: DisplayRecord[]) {
  const byKey = new Map(records.map((record) => [recordKey(record), record]));
  const members = item.memberKeys
    .map((key) => byKey.get(key))
    .filter((record): record is DisplayRecord => Boolean(record));
  return { records: caseAiRecords(members), links: item.links };
}

/** Cases worth narrating: several records, or one record with real history. */
export function caseStoryCandidates(graph: CaseGraph, records: DisplayRecord[], asOf: string) {
  const eligible = new Set(
    records.filter((record) => eligibleForAiSummary(record, asOf)).map(recordKey),
  );
  return graph.cases
    .filter((item) => item.memberKeys.some((key) => eligible.has(key)))
    .filter((item) => item.memberKeys.length >= 2 || item.events.length >= 3)
    .sort(
      (a, b) =>
        Number(b.status === 'open') - Number(a.status === 'open') ||
        b.lastActivityTime - a.lastActivityTime,
    )
    .map((item) => ({
      caseKey: item.key,
      ...caseStoryInput(item, records),
    }));
}

/** Stored stories keyed by case, only where the case still reads the same. */
export function caseStories(summary: PropertySummary, graph: CaseGraph, records: DisplayRecord[]) {
  const stories = new Map<string, CaseStoryEnrichment>();
  const stored = (summary.enrichments ?? []).filter(
    (item): item is CaseStoryEnrichment => item.kind === CASE_STORY_KIND,
  );
  if (!stored.length) return stories;
  const byCase = new Map(stored.map((item) => [item.caseKey, item]));
  const eligible = new Set(
    records.filter((record) => eligibleForAiSummary(record, summary.fetchedAt)).map(recordKey),
  );
  for (const item of graph.cases) {
    if (!item.memberKeys.some((key) => eligible.has(key))) continue;
    const story = byCase.get(item.key);
    if (!story) continue;
    const input = caseStoryInput(item, records);
    if (story.signature === caseSignature(item.key, input.records, input.links))
      stories.set(item.key, story);
  }
  return stories;
}
